import type { Session } from "@supabase/supabase-js";
import type { AuthProfile } from "@/hooks/useAuthState";
import { requireSupabase } from "@/lib/supabase";

export type Salesperson = {
  name: string;
  cell: string;
  email: string;
};

export function phoneDigits(input: string | null | undefined) {
  return String(input || "").replace(/\D/g, "").slice(0, 10);
}

export async function saveSignedInIdentity(args: {
  session: Session;
  profile: AuthProfile;
  nextSalesperson: Salesperson;
  refreshProfile: () => void;
  setAccountSaving: (v: boolean) => void;
  setAccountMessage: (msg: string) => void;
}) {
  const { session, profile, nextSalesperson, refreshProfile, setAccountSaving, setAccountMessage } = args;

  const nextFullName = nextSalesperson.name.trim();
  const nextEmail = nextSalesperson.email.trim().toLowerCase();
  const nextPhone = phoneDigits(nextSalesperson.cell);

  const currentFullName = (profile.full_name ?? "").trim();
  const currentEmail = (profile.email ?? session.user.email ?? "").trim().toLowerCase();
  const currentPhone = phoneDigits(profile.personal_phone ?? "");

  if (
    nextFullName === currentFullName &&
    nextEmail === currentEmail &&
    nextPhone === currentPhone
  ) {
    return;
  }

  if (!nextFullName) {
    setAccountMessage("Your account name cannot be blank.");
    return;
  }

  setAccountSaving(true);
  setAccountMessage("");

  try {
    const sb = requireSupabase();
    const emailChanged = !!nextEmail && nextEmail !== currentEmail;

    if (emailChanged || nextFullName !== currentFullName) {
      const { error: authError } = await sb.auth.updateUser({
        ...(emailChanged ? { email: nextEmail } : {}),
        data: {
          full_name: nextFullName,
          display_name: profile.display_name?.trim() || nextFullName,
          name: nextFullName,
        },
      });

      if (authError) throw authError;
    }

    const { error: profileError } = await sb
      .from("profiles")
      .update({
        full_name: nextFullName,
        display_name: profile.display_name?.trim() || nextFullName,
        email: nextEmail || null,
        personal_phone: nextPhone || null,
      })
      .eq("id", session.user.id);

    if (profileError) throw profileError;

    refreshProfile();
    setAccountMessage(
      emailChanged ? "Saved. Check your inbox to confirm the email change." : "Saved to your signed-in profile."
    );
  } catch (error) {
    setAccountMessage(error instanceof Error ? error.message : String(error));
  } finally {
    setAccountSaving(false);
  }
}
